import React from 'react';
import { Heart, Users, BookOpen, HomeIcon, Handshake, Church, ScrollText, HeartHandshake } from 'lucide-react';
import SEO from './shared/SEO';
import PageLayout from './layout/PageLayout';
import CTASection from './shared/CTASection';
import ServiceSection from './shared/ServicesSection';

const ServicesPage = () => {
  const services = {
    primary: [
      {
        icon: Heart,
        title: "Neverland Initiative",
        description: "Comprehensive support program for youth aged 11-30",
        features: [
          "Safe and inclusive environment",
          "Houseing and job assistance",
          "Community support network",
        ],
        highlight: "Our flagship program!",
        gradient: "from-purple-600 to-blue-600"
      },
      {
        icon: Users,
        title: "Counseling Services",
        description: "Professional guidance and support for individuals and families",
        features: [
          "Family counseling",
          "Pastoral counseling",
          "Mental health support"
        ],
        highlight: "Confidential and caring support",
        gradient: "from-blue-600 to-cyan-500"
      },
      {
        icon: BookOpen,
        title: "Religious Education",
        description: "Inclusive spiritual guidance and learning opportunities",
        features: [
          "Weekly study groups",
          "Youth programs",
          "Interfaith dialogue"
        ],
        highlight: "Open to all beliefs!",
        gradient: "from-cyan-500 to-green-500"
      }
    ],
    secondary: [
      {
        icon: HomeIcon,
        title: "Community Housing",
        description: "Safe housing support for displaced youth",
        gradient: "from-purple-500 to-pink-500"
      },
      {
        icon: Church,
        title: "Circle Services",
        description: "Twice-weekly spiritual gatherings and community meetings",
        gradient: "from-blue-500 to-indigo-500"
      },
      {
        icon: ScrollText,
        title: "Life Events",
        description: "Wedding ceremonies and memorial services",
        gradient: "from-teal-500 to-emerald-500"
      },
      {
        icon: HeartHandshake,
        title: "Support Programs",
        description: "Additional resources and community assistance",
        gradient: "from-orange-500 to-amber-500"
      }
    ]
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": services.primary.map((service, index) => ({
      "@type": "Service",
      "position": index + 1,
      "name": service.title,
      "description": service.description,
      "provider": {
        "@type": "Organization",
        "name": "COTAD",
        "nonprofitStatus": "501(c)(3)"
      }
    }))
  };

  return (
    <>
      <SEO
        title="Services & Programs | COTAD"
        description="Comprehensive support services including youth programs, counseling, religious education, and community services. An inclusive space for all."
        pathname="/services"
      >
        <script type="application/ld+json">
          {JSON.stringify(servicesSchema)}
        </script>
      </SEO>

      <PageLayout
        title="Our Services"
        description="Supporting our community through acceptance, education, and care"
      >
        {/* Primary Services */}
        <ServiceSection 
          services={services.primary}
          variant="primary"
          className="py-16"
        />

        {/* Secondary Services */}
        <ServiceSection 
          services={services.secondary}
          variant="secondary"
          columns={4}
          className="py-16"
        />

        <CTASection
          title="Need support or want to help?"
          description="Whether you're seeking assistance or wanting to contribute, we're here for you"
          primaryButtonText="Contact Us"
          primaryButtonLink="/contact"
          secondaryButtonText="Donate Now"
          secondaryButtonLink="/donate"
        />

        {/* Additional Information */}
        <section className="bg-bg-secondary py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-lg text-text-secondary">
                As a 501(c)(3) nonprofit organization, we rely on the generosity of our community to continue providing these essential services. All donations are tax-deductible and directly support our programs.
              </p>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
};

export default ServicesPage;